import { ControllerStore } from '@ayocore/platform/client/controllers/ControllerStore';
import type { UserSettingResult } from '../../common/userSettings';

export interface UserSettingOptions<T> {
  defaultValue: T;
  decode: (value: unknown, defaults: T) => T;
  loadDefaults?: () => Promise<T>;
}

interface SettingTransport {
  get: (key: string) => Promise<UserSettingResult>;
  set: (key: string, value: unknown) => Promise<void>;
  reset: (key: string) => Promise<void>;
}

interface SettingState<T> {
  value: T;
  loading: boolean;
  ready: boolean;
  saving: boolean;
  error: string | null;
}

/** Сохранение подтверждается сервером. Поздние ответы после смены пользователя игнорируются. */
export class UserSettingController<T> extends ControllerStore<SettingState<T>> {
  private generation = 0;
  private defaults: T;

  constructor(
    private key: string,
    private options: UserSettingOptions<T>,
    private transport: SettingTransport
  ) {
    super({ value: options.defaultValue, loading: true, ready: false, saving: false, error: null });
    this.defaults = options.defaultValue;
  }

  cancel = () => {
    this.generation += 1;
  };

  reload = async (): Promise<void> => {
    if (this.getSnapshot().saving) return;
    const generation = ++this.generation;
    this.setState({ loading: true, ready: false, error: null });
    try {
      const [stored, defaults] = await Promise.all([
        this.transport.get(this.key),
        this.options.loadDefaults?.() ?? this.options.defaultValue,
      ]);
      if (generation !== this.generation) return;
      this.defaults = defaults;
      this.setState({
        value: stored.found ? this.options.decode(stored.value, defaults) : defaults,
        loading: false,
        ready: true,
      });
    } catch {
      if (generation === this.generation)
        this.setState({ loading: false, error: 'Не удалось загрузить настройки' });
    }
  };

  setValue = (value: T): Promise<boolean> => this.persist(value, false);
  reset = (): Promise<boolean> => this.persist(this.defaults, true);

  private async persist(value: T, reset: boolean): Promise<boolean> {
    if (!this.getSnapshot().ready || this.getSnapshot().saving) return false;
    const generation = this.generation;
    this.setState({ saving: true, error: null });
    try {
      if (reset) await this.transport.reset(this.key);
      else await this.transport.set(this.key, value);
      if (generation !== this.generation) return false;
      this.setState({ value, saving: false });
      return true;
    } catch {
      if (generation === this.generation)
        this.setState({ saving: false, error: 'Не удалось сохранить настройки' });
      return false;
    }
  }
}
